import React from 'react';
import { View } from 'react-native';
import { ThemedText } from '@/components/themed-text';
import { Charge } from '@/types/charge';
import { formatCurrency } from '@/utils/format-currency';
import { formatDate } from '@/utils/format-date';
import { Ionicons } from '@expo/vector-icons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface RefundTimelineProps {
  charge: Charge;
}

export function RefundTimeline({ charge }: RefundTimelineProps) {
  const refundedAmount = charge.refundedAmount || 0;
  const chargeAmount = charge.amount;

  if (refundedAmount === 0) {
    return null;
  }

  const isFullyRefunded = refundedAmount >= chargeAmount;
  const refundPercentage = (refundedAmount / chargeAmount) * 100;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <ThemedText className="font-semibold text-lg">Histórico de Reembolsos</ThemedText>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <View className="gap-4">
          {/* Refund Summary */}
          <View className="bg-muted/30 rounded-lg border border-border p-4">
            <View className="mb-3 flex-row items-center justify-between">
              <View className="flex-row items-center gap-2">
                <Ionicons
                  name={isFullyRefunded ? 'checkmark-circle' : 'time-outline'}
                  size={20}
                  className={isFullyRefunded ? 'text-success-500' : 'text-warning-500'}
                />
                <ThemedText className="font-medium text-sm">
                  {isFullyRefunded ? 'Reembolso Completo' : 'Reembolso Parcial'}
                </ThemedText>
              </View>
              <ThemedText className="font-bold text-lg text-primary">
                {formatCurrency(refundedAmount)}
              </ThemedText>
            </View>

            {/* Progress Bar */}
            <View className="h-2 overflow-hidden rounded-full bg-neutral-200">
              <View
                className="h-full rounded-full bg-primary"
                style={{ width: `${Math.min(refundPercentage, 100)}%` }}
              />
            </View>

            <View className="mt-2 flex-row items-center justify-between">
              <ThemedText className="text-xs text-foreground/60">
                {refundPercentage.toFixed(1)}% do valor total
              </ThemedText>
              <ThemedText className="text-xs text-foreground/60">
                Restante: {formatCurrency(chargeAmount - refundedAmount)}
              </ThemedText>
            </View>
          </View>

          {/* Refund Details */}
          <View className="gap-2">
            <ThemedText className="mb-2 font-medium text-sm">Detalhes</ThemedText>
            <View className="flex-row items-center justify-between rounded-lg border border-border bg-card p-3">
              <View className="flex-row items-center gap-2">
                <Ionicons name="cash-outline" size={16} className="text-foreground/60" />
                <ThemedText className="text-sm">Valor Reembolsado</ThemedText>
              </View>
              <ThemedText className="font-semibold text-sm">
                {formatCurrency(refundedAmount)}
              </ThemedText>
            </View>
            <View className="flex-row items-center justify-between rounded-lg border border-border bg-card p-3">
              <View className="flex-row items-center gap-2">
                <Ionicons name="calendar-outline" size={16} className="text-foreground/60" />
                <ThemedText className="text-sm">Data do Reembolso</ThemedText>
              </View>
              <ThemedText className="text-sm">
                {charge.updatedAt ? formatDate(new Date(charge.updatedAt)) : 'N/A'}
              </ThemedText>
            </View>
          </View>
        </View>
      </CardContent>
    </Card>
  );
}
